import {
  GQCompanyClimateCommitmentKind,
  GQDataIssueState,
  GQExternalReportType,
  GQFootprintDisclosureGranularity,
  GQGhgScope,
  GQMarketplacePurchaseLineItemStatus,
  GQMarketplacePurchaseStatus,
  GQMarketplaceWaitingOnParty,
  GQSupplierChartType,
  GQWatershedPlanLegacy,
  GQSurveyDefinitionCategory,
  GQSbtCommitmentStage,
  GQTaskWatershedProcessStateSimplified,
} from '@watershed/shared-universal/generated/graphql-schema-types';
import { TargetKind } from '@watershed/constants/reductions';

import { GQCreateUserAdminSendUserEmails } from '@watershed/shared-universal/generated/graphql';

// NOTE: GraphQL codegen doesn't preserve declaration order, so this is where we
// put enums where order is significant when we want to call Object.keys/values
// on them for building UI

export const COMPANY_CLIMATE_COMMITMENT_KIND_ORDERED: {
  [key in GQCompanyClimateCommitmentKind]: GQCompanyClimateCommitmentKind;
} = {
  ScienceBasedTarget: GQCompanyClimateCommitmentKind.ScienceBasedTarget,
  NetZero: GQCompanyClimateCommitmentKind.NetZero,
  CarbonNeutral: GQCompanyClimateCommitmentKind.CarbonNeutral,
  CleanEnergy: GQCompanyClimateCommitmentKind.CleanEnergy,
};

export const PURCHASE_STATUSES_ORDERED: {
  [key in GQMarketplacePurchaseStatus]: GQMarketplacePurchaseStatus;
} = {
  CustomerInitiatedPurchase:
    GQMarketplacePurchaseStatus.CustomerInitiatedPurchase,
  CustomerRequestToWatershed:
    GQMarketplacePurchaseStatus.CustomerRequestToWatershed,
  WatershedConfirmedForCustomer:
    GQMarketplacePurchaseStatus.WatershedConfirmedForCustomer,
  CustomerConfirmedPurchase:
    GQMarketplacePurchaseStatus.CustomerConfirmedPurchase,
  WatershedSentContract: GQMarketplacePurchaseStatus.WatershedSentContract,
  CustomerSignedContract: GQMarketplacePurchaseStatus.CustomerSignedContract,
  WatershedSentInvoice: GQMarketplacePurchaseStatus.WatershedSentInvoice,
  CustomerPaidInvoiceAwaitingDelivery:
    GQMarketplacePurchaseStatus.CustomerPaidInvoiceAwaitingDelivery,
  WatershedPartiallyDelivered:
    GQMarketplacePurchaseStatus.WatershedPartiallyDelivered,
  WatershedFullyDelivered: GQMarketplacePurchaseStatus.WatershedFullyDelivered,
  CustomerCanceled: GQMarketplacePurchaseStatus.CustomerCanceled,
};

export const LINE_ITEM_STATUS_ORDERED: {
  [key in GQMarketplacePurchaseLineItemStatus]: GQMarketplacePurchaseLineItemStatus;
} = {
  Draft: GQMarketplacePurchaseLineItemStatus.Draft,
  WatershedSentRequestToSupplier:
    GQMarketplacePurchaseLineItemStatus.WatershedSentRequestToSupplier,
  SupplierSentInvoiceToWatershed:
    GQMarketplacePurchaseLineItemStatus.SupplierSentInvoiceToWatershed,
  WatershedPaidInvoiceToSupplier:
    GQMarketplacePurchaseLineItemStatus.WatershedPaidInvoiceToSupplier,
  SupplierDeliveredToWatershed:
    GQMarketplacePurchaseLineItemStatus.SupplierDeliveredToWatershed,
  WatershedDeliveredToCustomer:
    GQMarketplacePurchaseLineItemStatus.WatershedDeliveredToCustomer,
};

export const DATA_ISSUE_STATE_ORDERED = {
  NeedsUserResponse: GQDataIssueState.NeedsUserResponse,
  Done: GQDataIssueState.Done,
} as const;

export const CREATE_USER_ADMIN_SEND_USER_EMAILS_ORDERED: {
  [key in GQCreateUserAdminSendUserEmails]: GQCreateUserAdminSendUserEmails;
} = {
  None: GQCreateUserAdminSendUserEmails.None,
  FromWatershed: GQCreateUserAdminSendUserEmails.FromWatershed,
};

export const MARKETPLACE_WAITING_ON_PARTY_ORDER: {
  [key in GQMarketplaceWaitingOnParty]: GQMarketplaceWaitingOnParty;
} = {
  Watershed: GQMarketplaceWaitingOnParty.Watershed,
  Supplier: GQMarketplaceWaitingOnParty.Supplier,
  Customer: GQMarketplaceWaitingOnParty.Customer,
};

export const GHG_SCOPE_ORDERED: {
  [key in GQGhgScope]: GQGhgScope;
} = {
  Scope1: GQGhgScope.Scope1,
  Scope2: GQGhgScope.Scope2,
  Scope3: GQGhgScope.Scope3,
  CarbonRemoval: GQGhgScope.CarbonRemoval,
  AdditionalCleanPower: GQGhgScope.AdditionalCleanPower,
};

export const FOOTPRINT_DISCLOSURE_GRANULARITY_ORDERED: {
  [key in GQFootprintDisclosureGranularity]: GQFootprintDisclosureGranularity;
} = {
  Full: GQFootprintDisclosureGranularity.Full,
  AggregatesOnly: GQFootprintDisclosureGranularity.AggregatesOnly,
};

export const EDITABLE_EXTERNAL_REPORT_TYPE_ORDERED: {
  [key in Exclude<
    GQExternalReportType,
    'SBT' | 'CDP' | 'Other' // SBT & CDP are ingested and shouldn't be manually edited
  >]: GQExternalReportType;
} = {
  BCorp: GQExternalReportType.BCorp,
  CARB: GQExternalReportType.Carb,
  CSRD: GQExternalReportType.Csrd,
  DJSI: GQExternalReportType.Djsi,
  ESG: GQExternalReportType.Esg,
  Impact: GQExternalReportType.Impact,
  Marketing: GQExternalReportType.Marketing,
  MSCI: GQExternalReportType.Msci,
  SASB: GQExternalReportType.Sasb,
  SEC: GQExternalReportType.Sec,
  SECR: GQExternalReportType.Secr,
  Sustainalytics: GQExternalReportType.Sustainalytics,
  TCFD: GQExternalReportType.Tcfd,
  UkTender: GQExternalReportType.UkTender,
  EDCI: GQExternalReportType.Edci,
  SFDR: GQExternalReportType.Sfdr,
  SB253: GQExternalReportType.Sb253,
  UNFCCC: GQExternalReportType.Unfccc,
  EDGAR: GQExternalReportType.Edgar,
  ISSB: GQExternalReportType.Issb,
};

export const TARGET_KIND_ORDERED: {
  [key in TargetKind]: TargetKind;
} = {
  Offsets: TargetKind.Offsets,
  EmissionsReduction: TargetKind.EmissionsReduction,
  AvoidedEmissions: TargetKind.AvoidedEmissions,
  NetZero: TargetKind.NetZero,
};

export const WATERSHED_PLAN_ORDERED: {
  [key in GQWatershedPlanLegacy]: GQWatershedPlanLegacy;
} = {
  Standard: GQWatershedPlanLegacy.Standard,
  NetZero: GQWatershedPlanLegacy.NetZero,
  NoPlan: GQWatershedPlanLegacy.NoPlan,
  Lite: GQWatershedPlanLegacy.Lite,
  Pro: GQWatershedPlanLegacy.Pro,
};

export const SUPPLIER_CHART_TYPES_ORDERED: {
  [key in GQSupplierChartType]: GQSupplierChartType;
} = {
  Donut: GQSupplierChartType.Donut,
  EmissionsForecast: GQSupplierChartType.EmissionsForecast,
  EngagementFunnel: GQSupplierChartType.EngagementFunnel,
  HorizontalBar: GQSupplierChartType.HorizontalBar,
  TasksStatusChangeOverTime: GQSupplierChartType.TasksStatusChangeOverTime,
  Metrics: GQSupplierChartType.Metrics,
  SbtCommitmentOverTime: GQSupplierChartType.SbtCommitmentOverTime,
};

export const SURVEY_DEFINITION_CATEGORIES_ORDERED: {
  [key in GQSurveyDefinitionCategory]: GQSurveyDefinitionCategory;
} = {
  DataCollection: GQSurveyDefinitionCategory.DataCollection,
  Learning: GQSurveyDefinitionCategory.Learning,
  ScienceBasedTargets: GQSurveyDefinitionCategory.ScienceBasedTargets,
  ReductionProjects: GQSurveyDefinitionCategory.ReductionProjects,
  MakeCommitments: GQSurveyDefinitionCategory.MakeCommitments,
  Other: GQSurveyDefinitionCategory.Other,
};

export const SBT_COMMITMENT_STATUSES_ORDERED: {
  [key in GQSbtCommitmentStage]: GQSbtCommitmentStage;
} = {
  Committed: GQSbtCommitmentStage.Committed,
  TargetsSet: GQSbtCommitmentStage.TargetsSet,
  Removed: GQSbtCommitmentStage.Removed,
};

export const TASK_WATERSHED_PROCESS_STATE_SIMPLIFIED_ORDERED = {
  NotStarted: GQTaskWatershedProcessStateSimplified.NotStarted,
  InProgress: GQTaskWatershedProcessStateSimplified.InProgress,
  NeedsApproval: GQTaskWatershedProcessStateSimplified.NeedsApproval,
  Completed: GQTaskWatershedProcessStateSimplified.Completed,
  None: GQTaskWatershedProcessStateSimplified.None,
};
